// ReminderComponents.jsx - Glassmorphic UI components for the reminder system

import React, { useState, useEffect } from 'react';
import { Clock, X, Check, Bell, Timer, Calendar, Plus } from 'lucide-react';
import ReminderService from '../services/ReminderService';

// Glassmorphic Card Component
const GlassmorphicCard = ({ children, className = '', gradient = "grad1", opacity = 0.08, strokeOpacity = 0.15, showCloseBtn = false, onClose }) => (
  <div className={`glassmorphic-card ${className}`}>
    <svg className="glassmorphic-svg">
      <defs>
        <linearGradient id={gradient} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: `rgba(120,180,255,${opacity})`}}></stop>
          <stop offset="50%" style={{stopColor: `rgba(140,200,255,${opacity * 0.8})`}}></stop>
          <stop offset="100%" style={{stopColor: `rgba(160,220,255,${opacity * 0.9})`}}></stop>
        </linearGradient>
    <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style={{ stopColor: 'rgba(121, 145, 167, 0.82)' }} />
      <stop offset="50%" style={{ stopColor: 'rgba(147, 192, 223, 0.83)' }} />
      <stop offset="100%" style={{ stopColor: 'rgba(255, 255, 255, 0.86)' }} />
    </linearGradient>
      </defs>
      <path
        d="M 0,40 C 0,0 0,0 40,0 L 340,0 C 380,0 380,0 380,40 C 380,80 380,80 340,80 L 40,80 C 0,80 0,80 0,40 Z"
        fill={`url(#${gradient})`}
        stroke={`rgba(160,200,255,${strokeOpacity})`}
        strokeWidth="1"
        className="glassmorphic-path"
      />
    </svg>
    {showCloseBtn && (
      <button 
        className="reminder-close-btn-outside"
        onClick={onClose}
      >
        <X size={14} />
      </button>
    )}
    <div className="glassmorphic-content">
      {children}
    </div>
  </div>
);

// Reminder Set Confirmation Modal
export const ReminderSetModal = ({ reminder, onClose, isVisible }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShow(true);
      const timer = setTimeout(() => {
        setShow(false);
        setTimeout(onClose, 300);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible && !show) return null;

  return (
    <div className={`reminder-modal-overlay ${show ? 'show' : 'hide'}`}>
      <GlassmorphicCard 
        className={`reminder-set-modal ${show ? 'slide-down' : 'slide-up'}`} 
        gradient="successGrad" 
        opacity={0.12}
        showCloseBtn={true}
        onClose={() => {
          setShow(false);
          setTimeout(onClose, 300);
        }}
      >
        <div className="reminder-avatar success-avatar">
          <Check size={20} />
        </div>
        
        <div className="reminder-body">
          <div className="reminder-header">
            <div className="reminder-title">Reminder Set!</div>
            <div className="reminder-time">now</div>
          </div>
          <div className="reminder-message">
            {reminder?.description || 'Reminder scheduled successfully'}
          </div>
          {reminder?.message && reminder.message !== reminder.description && (
            <div className="reminder-meta">
              "{reminder.message}"
            </div>
          )}
        </div>
      </GlassmorphicCard>
    </div>
  );
};

// Reminder Triggered Modal
export const ReminderTriggeredModal = ({ reminder, onClose, onSnooze, isVisible }) => {
  const [show, setShow] = useState(false);
  const [isBlinking, setIsBlinking] = useState(true);

  useEffect(() => {
    if (isVisible) {
      setShow(true);
      const blinkTimer = setTimeout(() => {
        setIsBlinking(false);
      }, 10000);
      return () => clearTimeout(blinkTimer);
    } else {
      setShow(false);
    }
  }, [isVisible]);

  const handleSnooze = (minutes) => {
    onSnooze(minutes);
    setShow(false);
    setTimeout(onClose, 300);
  };

  const handleDismiss = () => {
    setShow(false);
    setTimeout(onClose, 300);
  };

  if (!isVisible && !show) return null;

  return (
    <div className={`reminder-modal-overlay ${show ? 'show' : 'hide'}`}>
      <GlassmorphicCard 
        className={`reminder-triggered-modal ${show ? 'slide-down' : 'slide-up'} ${isBlinking ? 'blinking' : ''}`} 
        gradient="alertGrad" 
        opacity={0.15}
        showCloseBtn={true}
        onClose={handleDismiss}
      >
        <div className="reminder-avatar alert-avatar">
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="m10.827 5.465-.435-2.324m.435 2.324a5.338 5.338 0 0 1 6.033 4.333l.331 1.769c.44 2.345 2.383 2.588 2.6 3.761.11.586.22 1.171-.31 1.271l-12.7 2.377c-.529.099-.639-.488-.749-1.074C5.813 16.73 7.538 15.8 7.1 13.455c-.219-1.169.218 1.162-.33-1.769a5.338 5.338 0 0 1 4.058-6.221Zm-7.046 4.41c.143-1.877.822-3.461 2.086-4.856m2.646 13.633a3.472 3.472 0 0 0 6.728-.777l.09-.5-6.818 1.277Z"/>
</svg>
        </div>
        
        <div className="reminder-body">
          <div className="reminder-header">
            <div className="reminder-title">Reminder</div>
          </div>
          <div className="reminder-message alert-message">
            {reminder?.message || 'Your reminder is ready'}
          </div>
        </div>
        
        <div className="reminder-actions">
          <button 
            className="reminder-action-btn snooze-btn"
            onClick={() => handleSnooze(5)}
          >
            <Timer size={14} />
            5min
          </button>
          
          <button 
            className="reminder-action-btn dismiss-btn"
            onClick={handleDismiss}
          >
            <Check size={14} />
          </button>
        </div>
      </GlassmorphicCard>
    </div>
  );
};

// Active Reminders Container
export const ActiveRemindersContainer = ({ reminders, onCancel }) => {
  if (!reminders || reminders.length === 0) return null;

  return (
    <div className="reminder-container">
      {reminders.slice(0, 3).map((reminder, index) => {
        const zIndex = 30 - (index * 10);
        const scale = 1 - (index * 0.06);
        const opacity = 1 - (index * 0.25);
        const top = index * 20;
        
        return (
          <div 
            key={reminder.id} 
            className={`reminder-card reminder-${index === 0 ? 'top' : index === 1 ? 'middle' : 'bottom'}`}
            style={{
              zIndex,
              transform: `scale(${scale})`,
              opacity,
              top: `${top}px`
            }}
          >
            <GlassmorphicCard gradient={`grad${index + 1}`} opacity={0.08 - (index * 0.02)} strokeOpacity={0.15 - (index * 0.03)}>
              <div className="reminder-avatar">
                <Clock size={18} />
              </div>
              
              <div className="reminder-body">
                <div className="reminder-header">
                  <div className="reminder-title">{reminder.description}</div>
                  <div className="reminder-time">
                    {new Date(reminder.triggerTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
                <div className="reminder-message">
                  {reminder.message || 'Scheduled reminder'}
                </div>
                {reminder.message && reminder.message !== reminder.description && (
                  <div className="reminder-meta">
                    Active • {Math.ceil((reminder.triggerTime - Date.now()) / 60000)}min remaining
                  </div>
                )}
              </div>
              
              <button 
                className="reminder-cancel-btn"
                onClick={() => onCancel(reminder.id)}
                title="Cancel reminder"
              >
                <X size={14} />
              </button>
            </GlassmorphicCard>
          </div>
        );
      })}
    </div>
  );
};

// Reminder Input Section
export const ReminderInputSection = ({ onSetReminder }) => {
  const [newReminderText, setNewReminderText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSetReminder = () => {
    if (!newReminderText.trim()) {
      setError('Please enter a reminder time');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      onSetReminder(newReminderText);
      setNewReminderText('');
      setSuccess('Reminder set!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
      <div className="reminder-input-section">
        <h4>Set New Reminder</h4>
        <div className="reminder-input-container">
          <input
            type="text"
            placeholder="e.g., 'in 5 minutes', 'at 3:30 PM', 'tomorrow at 9 AM'"
            value={newReminderText}
            onChange={(e) => setNewReminderText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSetReminder()}
            className="reminder-input"
          />
          <button 
            onClick={handleSetReminder}
            className="reminder-set-btn"
          >
            <Bell size={16} />
            Set Reminder
          </button>
        </div>
        
        {error && (
          <div className="reminder-error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="reminder-success">
            {success}
          </div>
        )}
      </div>
  );
};

// Main Reminder Tab Content
export const ReminderTabContent = ({ reminders, onCancel, reminderService }) => {
  const handleSetReminder = (reminderText) => {
    const extracted = ReminderService.extractReminderFromMessage(reminderText);
    const timeExpression = extracted.timeExpression || reminderText;
    const message = extracted.message;
    reminderService.setReminder(timeExpression, message);
  };

  return (
    <div className="reminder-tab-content">
      <div className="reminder-layout">
        <ReminderInputSection onSetReminder={handleSetReminder} />
        <ActiveRemindersContainer 
          reminders={reminders} 
          onCancel={onCancel}
        />
      </div>
    </div>
  );
};